import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { selectUserToken } from "../../features/user";
import permissaoApi from "../../utility/api/permissaoApi";
import useQuery from "../../utility/useQuery";
import Swal from "sweetalert2";
import Dropdown from "../../components/Dropdown";
import Button from "../../components/Button";
import Modal from "../../components/Modal";
import Input from "../../components/Input";
import Toggle from "../../components/Toggle";
import ButtonClasses from "../../components/Button/typing";
import CancelarButton from "../../components/CancelarButton";
import moduloApi from "../../utility/api/moduloApi";
import { NotAllowed, permissoes, useAthorize } from "../../hooks/useAthorize";

const EdicaoPermissao = ({ setIsVisible, onFinished }) => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm();

  let isAllowed = useAthorize(
    permissoes.usuarioConsulta,
    permissoes.usuarioFull
  );

  const query = useQuery();
  let permissaoId = parseInt(query.get("permissaoId") ?? 0);

  const token = useSelector(selectUserToken);
  const [modulos, setModulos] = useState([]);
  const [permissao, setPermissao] = useState({});

  const onSubmit = useCallback(
    async ({ moduloId, nome, nomeAlternativo, descricao, status }) => {
      let data = {
        id: permissaoId,
        moduloId,
        nome,
        nomeAlternativo,
        descricao,
        isDisabled: !status,
      };

      let result = await permissaoApi.atualizarPermissao(data, token);

      if (result.hasError) {
        Swal.fire({
          title: "Erro!",
          text: "Algo deu errado",
          icon: "error",
        });
        return;
      }

      if (result.data.length === 0) {
        reset({
          ...data,
          status,
        });
        onFinished?.();

        Swal.fire({
          title: "Sucesso!",
          text: "Editado com sucesso",
          icon: "success",
        });
        return;
      }

      Swal.fire({
        title: "Erro!",
        text: result.data,
        icon: "error",
      });
    },
    [permissaoId, onFinished, reset, token]
  );

  const fetchModulos = useCallback(async () => {
    var result = await moduloApi.listarDropdown({}, token);

    if (result.hasError) {
      Swal.fire({
        title: "Erro!",
        text: "Algo deu errado",
        icon: "error",
      });
      return;
    }

    setModulos(result.data);
  }, [token]);

  const fetchPermissao = useCallback(async () => {
    var result = await permissaoApi.buscarPermissao(permissaoId, token);

    if (result.hasError) {
      Swal.fire({
        title: "Erro!",
        text: "Algo deu errado",
        icon: "error",
      });
      return;
    }

    reset({
      ...result.data,
      status: result.data.status === "Ativo",
    });
    setPermissao(result.data);
  }, [permissaoId, reset, token]);

  useEffect(() => {
    fetchModulos();
    fetchPermissao();
  }, [fetchPermissao, fetchModulos]);

  var footer = (
    <>
      <CancelarButton
        watch={watch}
        reset={() =>
          Swal.fire({
            text: "Realmente deseja cancelar o cadastro?",
            showDenyButton: true,
            confirmButtonText: "Sim",
            denyButtonText: `Não`,
          }).then(async (result) => {
            if (result.isConfirmed) {
              reset();

              Swal.fire({
                text: "Cancelado com sucesso!",
                icon: "success",
              });
            }
          })
        }
        getValues={getValues}
        voltar={() => setIsVisible()}
      />
      <Button
        colorClass={ButtonClasses.DarkGreen}
        text="Salvar"
        onClick={handleSubmit(onSubmit)}
      />
    </>
  );

  if (!isAllowed) return <NotAllowed />;

  return (
    <Modal
      setModal={setIsVisible}
      title="Edição de Permissão"
      footer={footer}
      height="h-auto"
      heightBody="h-[64%] py-6"
    >
      <div className="w-full h-full flex justify-center items-center">
        <form onSubmit={handleSubmit(onSubmit)} className="w-[86%]">
          <div className="grid grid-cols-1 gap-8">
            <Dropdown
              name="moduloId"
              defaultText="Selecione o Módulo"
              register={register}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
              options={modulos}
              width={"w-full"}
              label="Módulo"
              errors={errors}
              required
            />

            <Input
              label="Nome"
              name="nome"
              type="text"
              placeholder="Insira o nome da permissão"
              register={register}
              required
              errors={errors}
            />

            <Input
              label="Nome Alterantivo"
              name="nomeAlternativo"
              type="text"
              placeholder="Insira o nome alternativo da permissão"
              register={register}
              required
              errors={errors}
            />

            <Input
              label="Descrição"
              name="descricao"
              type="text"
              placeholder="Insira a descricao da permissão"
              register={register}
              errors={errors}
              multiline
            />
          </div>
          <div className="pt-2">
            <Toggle
              register={register}
              setValue={setValue}
              watch={watch}
              name="status"
              defaultValue={permissao.status}
            />
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EdicaoPermissao;
